/**
 * AK-938 — редактор вариантов опыта: роль, «что делаем» и препараты с дозой.
 *
 * Один компонент на два места: форма схемы (состав вариантов правится) и шаг 2
 * «Заложить опыт» — правка копии для этого поля (`lockStructure`: варианты и
 * роли от схемы, меняются только препараты, дозы, сроки; схема не меняется).
 * Препараты — только из системного справочника СЗР (founder 16.10), поиск на
 * сервере, список по алфавиту.
 */
import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useProducts, useUnits } from '@/lib/pesticides/queries';
import { ROLE_LABELS, variantMarks } from '@/lib/science/labels';
import {
  draftKey,
  emptyVariant,
  type DraftErrors,
  type InputDraft,
  type VariantDraft,
} from '@/lib/science/schemeDraft';
import type { VariantRole } from '@/lib/science/types';
import { PickList, type PickItem } from './PickList';

function ProductPicker({
  input,
  invalid,
  onPick,
  disabled,
}: {
  input: InputDraft;
  invalid: boolean;
  onPick: (id: string, name: string) => void;
  disabled?: boolean;
}) {
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQ(typed), 250);
    return () => clearTimeout(t);
  }, [typed]);
  const products = useProducts({ q: q || undefined, limit: 50 }, true);
  const items: PickItem[] = (products.data?.items ?? []).map((p) => ({
    value: p.id,
    label: p.formulation ? `${p.name}, ${p.formulation}` : p.name,
  }));
  if (input.product_kind === 'fertilizer') {
    // Удобрения из справочника веб пока не ищет (нет ручки поиска) — строку
    // схемы показываем как есть, без замены.
    return (
      <Input value={input.product_name || 'удобрение'} disabled aria-label="Удобрение" />
    );
  }
  return (
    <PickList
      aria-label="Препарат"
      value={input.product_id || null}
      selectedLabel={input.product_name}
      items={items}
      loading={products.isFetching}
      onSearch={setTyped}
      onChange={(id, item) => onPick(id, item.label)}
      placeholder="Препарат из справочника СЗР"
      searchPlaceholder="Название препарата"
      emptyText={q ? 'Препарат не найден в справочнике' : 'Начните вводить название'}
      invalid={invalid}
      disabled={disabled}
    />
  );
}

function InputRow({
  input,
  error,
  unitItems,
  onChange,
  onRemove,
  disabled,
}: {
  input: InputDraft;
  error?: string;
  unitItems: PickItem[];
  onChange: (next: InputDraft) => void;
  onRemove: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-[minmax(0,2fr)_6rem_minmax(0,1fr)_minmax(0,1fr)_auto] md:items-center">
        <ProductPicker
          input={input}
          invalid={!!error && !input.product_id}
          disabled={disabled}
          onPick={(id, name) => onChange({ ...input, product_id: id, product_name: name })}
        />
        <Input
          aria-label="Доза"
          inputMode="decimal"
          placeholder="доза"
          value={input.rateText}
          disabled={disabled}
          aria-invalid={!!error || undefined}
          className={error ? 'border-destructive' : undefined}
          onChange={(e) => onChange({ ...input, rateText: e.target.value })}
        />
        <PickList
          aria-label="Единица"
          value={input.rate_unit_id}
          selectedLabel={input.rate_unit_symbol}
          items={unitItems}
          disabled={disabled}
          onChange={(id, item) =>
            onChange({ ...input, rate_unit_id: id, rate_unit_symbol: item.label })
          }
          placeholder="ед."
          searchPlaceholder="л/га, г/га…"
        />
        <Input
          aria-label="Срок применения"
          placeholder="срок, например кущение"
          value={input.timing_note}
          disabled={disabled}
          onChange={(e) => onChange({ ...input, timing_note: e.target.value })}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Убрать препарат"
          disabled={disabled}
          onClick={onRemove}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

interface Props {
  variants: VariantDraft[];
  onChange: (next: VariantDraft[]) => void;
  errors: DraftErrors;
  /** Шаг «правка копии»: состав и роли от схемы, меняются только препараты. */
  lockStructure?: boolean;
  disabled?: boolean;
}

export function VariantsEditor({ variants, onChange, errors, lockStructure, disabled }: Props) {
  const units = useUnits();
  const unitItems = useMemo<PickItem[]>(
    () =>
      (units.data ?? []).map((u) => ({
        value: u.id,
        label: u.symbol,
        hint: u.name_ru,
      })),
    [units.data],
  );
  const marks = variantMarks(variants.map((v) => v.role));

  function patch(idx: number, next: Partial<VariantDraft>) {
    onChange(variants.map((v, i) => (i === idx ? { ...v, ...next } : v)));
  }

  function addVariant() {
    const tests = variants.filter((v) => v.role === 'test').length;
    onChange([...variants, emptyVariant('test', `Вариант ${tests + 1}`)]);
  }

  function addInput(idx: number) {
    const v = variants[idx];
    const blank: InputDraft = {
      key: draftKey('i'),
      product_kind: 'pesticide',
      product_id: '',
      product_name: '',
      rateText: '',
      rate_unit_id: null,
      rate_unit_symbol: null,
      timing_note: '',
    };
    patch(idx, { inputs: [...v.inputs, blank] });
  }

  return (
    <div className="flex flex-col gap-4">
      {variants.map((v, idx) => (
        <div key={v.key} className="flex flex-col gap-3 rounded-lg border border-border p-3">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-[2rem_minmax(0,2fr)_minmax(0,1fr)_auto] md:items-center">
            <span className="text-sm font-semibold text-muted-foreground">{marks[idx]}</span>
            <Input
              aria-label="Название варианта"
              value={v.name}
              disabled={disabled || lockStructure}
              aria-invalid={!!errors[v.key] || undefined}
              className={errors[v.key] ? 'border-destructive' : undefined}
              onChange={(e) => patch(idx, { name: e.target.value })}
            />
            <Select
              value={v.role}
              disabled={disabled || lockStructure}
              onValueChange={(r) => patch(idx, { role: r as VariantRole })}
            >
              <SelectTrigger aria-label="Роль варианта">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(['control', 'reference', 'test'] as VariantRole[]).map((r) => (
                  <SelectItem key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!lockStructure && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Убрать вариант"
                disabled={disabled || variants.length <= 1}
                onClick={() => onChange(variants.filter((_, i) => i !== idx))}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
          {errors[v.key] && (
            <p role="alert" className="text-xs text-destructive">
              {errors[v.key]}
            </p>
          )}
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-muted-foreground">
              Что делаем (сорт, обработка почвы, срок) — если без препарата
            </Label>
            <Input
              aria-label="Что делаем"
              value={v.note}
              disabled={disabled}
              placeholder={v.role === 'control' ? 'например: без гербицида' : ''}
              onChange={(e) => patch(idx, { note: e.target.value })}
            />
          </div>
          {v.inputs.map((inp) => (
            <InputRow
              key={inp.key}
              input={inp}
              error={errors[inp.key]}
              unitItems={unitItems}
              disabled={disabled}
              onChange={(next) =>
                patch(idx, { inputs: v.inputs.map((x) => (x.key === inp.key ? next : x)) })
              }
              onRemove={() => patch(idx, { inputs: v.inputs.filter((x) => x.key !== inp.key) })}
            />
          ))}
          <div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              onClick={() => addInput(idx)}
            >
              <Plus className="h-4 w-4" />
              Добавить препарат
            </Button>
          </div>
        </div>
      ))}
      {!lockStructure && (
        <div>
          <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={addVariant}>
            <Plus className="h-4 w-4" />
            Добавить вариант
          </Button>
        </div>
      )}
      {errors.roles && (
        <p role="alert" className="text-sm text-destructive">
          {errors.roles}
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        Контроль — ровно один, эталон — не больше одного, опытных — хотя бы один. Препараты — из
        справочника СЗР.
      </p>
    </div>
  );
}
